import { Injectable, inject } from '@angular/core';
import {
	CustomErrorMessages,
	CustomNotification,
	CustomNotificationType,
	DataMigrationTypesEnum,
	Languages,
	ltrDayMonthFormat,
	MappedNotification,
	migrationExportTypes,
	Notification,
	NotificationIconType,
	NotificationOpenModalAction,
	NotificationRedirectType,
	NotificationTypeEnum,
	NotificationTypesModel,
	rtlDayMonthFormat,
	RunMigrationUrl,
	ToastService
} from '@core';
import { TranslateService } from '@ngx-translate/core';
import { Params, Router } from '@angular/router';
import { MainRoutes } from '@app/shared/enums/general.enums';
import { TasksRoute } from '@app/pages/tasks/enums/tasks.enums';
import { PassDataKeys, PassDataService } from '@app/core/services/pass-data/pass-data.service';
import { FinanceRoutes } from '@app/modules/views/management/management/models/management.enums';
import { ModalService, ModalType } from '@ui';
import { ClientsAndMattersRoute } from '@app/pages/clients-and-matters/enums/clients-and-matters.enums';
import { NotificationActionsService } from '@app/services/notification-actions/notification-actions.service';
import { format, getHours, getMinutes, isToday, isYesterday } from 'date-fns';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { FirmRegion } from '@app/core/services/settings/settings.enum';
import { tap } from 'rxjs/operators';
import { MigrationsDataService } from '@app/services/migrations/migrations-data.service';
import { ReportModalConfig } from '@app/components/report-form-config.type';
import { NewEntryService } from '@app/shared/services/new-entry/new-entry.service';
import { NotificationSettingsService } from '@app/services/notification-settings/notification-settings.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ErrorCode, ToastType, UrlType } from '@core/services/toast-service/enums';
import { ToastPayload, ToastUrl } from '@core/services/toast-service/models';
import { MatterAutocompleteV3Option } from '@app/core/services/matters/matters.model';

const MAP_CUSTOM_NOTIFICATION_TOAST_TYPE: Partial<Record<CustomNotification, ToastType>> = {
	[CustomNotification.Projects]: ToastType.Success,
	[CustomNotification.Routine]: ToastType.Default,
	[CustomNotification.DocTools]: ToastType.Success
};

type NotificationIconName = 'attention' | 'bell' | 'report';

const MAP_CUSTOM_NOTIFICATION_ICON_TYPE: Partial<Record<CustomNotification, NotificationIconName>> = {
	[CustomNotification.Projects]: 'bell',
	[CustomNotification.Routine]: 'bell',
	[CustomNotification.DocTools]: 'report'
};

const MAP_NOTIFICATION_TOAST_TYPE: Partial<Record<NotificationTypeEnum, ToastType>> = {
	[NotificationTypeEnum.Custom]: ToastType.Success,
	[NotificationTypeEnum.Pdf]: ToastType.Success,
	[NotificationTypeEnum.Accounting]: ToastType.Success,
	[NotificationTypeEnum.Excel]: ToastType.Success,
	[NotificationTypeEnum.Reminder]: ToastType.Default,
	[NotificationTypeEnum.ProformaInvoicesGreenLane]: ToastType.Success,
	[NotificationTypeEnum.ProformaInvoices]: ToastType.Success,
	[NotificationTypeEnum.Email]: ToastType.Success,
	[NotificationTypeEnum.Admin]: ToastType.Success,
	[NotificationTypeEnum.Migration]: ToastType.Success,
	[NotificationTypeEnum.MigrationCheckFail]: ToastType.Warning,
	[NotificationTypeEnum.MigrationCheck]: ToastType.Warning,
	[NotificationTypeEnum.Ledes]: ToastType.Success,
	[NotificationTypeEnum.ClientsMatters]: ToastType.Success,
	[NotificationTypeEnum.Matters]: ToastType.Success,
	[NotificationTypeEnum.EmailError]: ToastType.Error,
	[NotificationTypeEnum.ConflictCheck]: ToastType.Success
};

@Injectable({
	providedIn: 'root'
})
export class NotificationService {
	private notificationActionService = inject(NotificationActionsService);
	private notificationSettingsService = inject(NotificationSettingsService);
	private migrationsDataService = inject(MigrationsDataService);
	private settingsService = inject(SettingsService);
	private toastService = inject(ToastService);
	private translationService = inject(TranslateService);
	private passDataService = inject(PassDataService);
	private modalService = inject(ModalService);
	private router = inject(Router);
	private newEntryService = inject(NewEntryService);

	private readonly displayFormat: string;
	private readonly currentLang: string;

	private notificationsUpdated$: BehaviorSubject<MappedNotification> = new BehaviorSubject<MappedNotification>(null);

	constructor() {
		this.currentLang = this.translationService.currentLang;
		this.displayFormat =
			this.settingsService.getFirm()?.firm_region === FirmRegion.Usa ? ltrDayMonthFormat : rtlDayMonthFormat;
		this.toastService.onAction$.pipe().subscribe((notification: MappedNotification) => {
			if (!notification.read) {
				notification.read = true;
				this.markAsRead(notification);
				this.notificationSettingsService.markNotificationAsRead(notification.id);
			}
			// TODO: Implement and remove urlCallback
			// if (notification.canMigrated) {
			//   // this.runMigrationFile(notification);
			// }
		});
	}

	get notification$(): Observable<MappedNotification> {
		return this.notificationsUpdated$.asObservable();
	}

	private markAsRead(notification: MappedNotification) {
		this.notificationsUpdated$.next(notification);
	}

	private getTitleAndMessage(notification: MappedNotification): { title: string; message: string } {
		let { title, message, interpolateParams }: { title: string; message: string; interpolateParams: Params | null } = {
			title: '',
			message: '',
			interpolateParams: null
		};

		if (notification.canMigrated) {
			if (notification.actualType === NotificationTypeEnum.MigrationCheckFail) {
				title = this.translationService.instant('NEW_NOTIFICATIONS.MIGRATION_FILE_WITH_ERRORS.TITLE');
				message = this.translationService.instant('NEW_NOTIFICATIONS.MIGRATION_FILE_WITH_ERRORS.MESSAGE');
			} else {
				if (notification.actualType !== NotificationTypeEnum.Migration) {
					title = this.translationService.instant('NEW_NOTIFICATIONS.CAN_MIGRATED.TITLE');
					message = this.translationService.instant('NEW_NOTIFICATIONS.CAN_MIGRATED.MESSAGE');
				} else {
					title = this.translationService.instant('NEW_NOTIFICATIONS.MIGRATION_SUCCESS.TITLE');
					message = this.translationService.instant('NEW_NOTIFICATIONS.MIGRATION_SUCCESS.MESSAGE');
				}
			}
		} else {
			switch (notification.actualType) {
				case NotificationTypeEnum.Custom:
					const customNotificationText: { title: string; message: string } =
						this.getCustomNotificationTitleAndMessage(notification);
					title = customNotificationText.title;
					message = customNotificationText.message;
					break;
				case NotificationTypeEnum.MigrationCheck:
					title = this.translationService.instant('NEW_NOTIFICATIONS.MIGRATION_CHECK_SUCCESS');
					break;
				case NotificationTypeEnum.Reminder:
					title = this.translationService.instant('NEW_NOTIFICATIONS.REMINDER.TITLE');
					message = notification.message.task_name ?? notification.message.sub_task_name;
					break;
				case NotificationTypeEnum.ProformaInvoicesGreenLane:
					interpolateParams = {
						amount: notification.message.pis_sent
					};
					title = this.translationService.instant(
						'NEW_NOTIFICATIONS.VIEW_INVOICES_CREATED_AND_SENT.TITLE',
						interpolateParams
					);
					message = this.translationService.instant('NEW_NOTIFICATIONS.VIEW_INVOICES_CREATED_AND_SENT.MESSAGE');
					break;
				case NotificationTypeEnum.ProformaInvoices:
					interpolateParams = {
						amount: notification.message.pis_created
					};
					title = this.translationService.instant('NEW_NOTIFICATIONS.VIEW_INVOICES_CREATED.TITLE', interpolateParams);
					message = this.translationService.instant('NEW_NOTIFICATIONS.VIEW_INVOICES_CREATED.MESSAGE');
					break;
				case NotificationTypeEnum.Email:
					message = this.translationService.instant('NEW_NOTIFICATIONS.EMAIL_SUCCESS');
					break;
				case NotificationTypeEnum.Admin:
					message = this.translationService.instant('NEW_NOTIFICATIONS.ACTION_COMPLETED');
					break;
				case NotificationTypeEnum.ClientsMatters:
					if (notification.message.client && notification.message.redirect === NotificationRedirectType.NewClient) {
						interpolateParams = {
							name: notification.message.client.name,
							iid: notification.message.client.internal_id
						};
						title = this.translationService.instant(
							'NEW_NOTIFICATIONS.CLIENT_CREATED_SUCCESSFULLY.TITLE',
							interpolateParams
						);
					}
					break;
				case NotificationTypeEnum.Matters:
					if (notification.message.matter && notification.message.redirect === NotificationRedirectType.NewMatter) {
						interpolateParams = {
							name: notification.message.matter.name,
							iid: notification.message.matter.internal_id
						};
						title = this.translationService.instant(
							'NEW_NOTIFICATIONS.MATTER_CREATED_SUCCESSFULLY.TITLE',
							interpolateParams
						);
					}
					break;
				case NotificationTypeEnum.ConflictCheck:
					title = this.translationService.instant('NEW_NOTIFICATIONS.CONFLICT_CHECK_READY');
					break;
				case NotificationTypeEnum.EmailError:
					title = this.translationService.instant('NEW_NOTIFICATIONS.DOCUMENTS_READY_TOAST.TITLE');
					message = this.translationService.instant('NEW_NOTIFICATIONS.DOCUMENTS_READY_TOAST.MESSAGE');
					break;
				case NotificationTypeEnum.Accounting:
					title = this.translationService.instant('NEW_NOTIFICATIONS.DOCUMENTS_READY_TOAST.TITLE');
					message = this.translationService.instant('NEW_NOTIFICATIONS.DOCUMENTS_READY_TOAST.MESSAGE');
					break;
				default:
					title = this.translationService.instant('NEW_NOTIFICATIONS.DOCUMENT_READY_TOAST.TITLE');
					message = this.translationService.instant('NEW_NOTIFICATIONS.DOCUMENT_READY_TOAST.MESSAGE');
			}
		}
		return { title, message };
	}

	private getCustomNotificationTitleAndMessage(notification: MappedNotification): { title: string; message: string } {
		const [[key, value]] = Object.entries(notification.message.message_type);

		const routineTitleMap: Record<string, string> = {
			good_morning: 'NEW_NOTIFICATIONS.GOOD_MORNING',
			daily: 'NEW_NOTIFICATIONS.DAILY',
			stretch: 'NEW_NOTIFICATIONS.STRETCH'
		};

		const NOTIFICATION_TO_TITLE_MAP: Record<CustomNotification, { title: string; message: string }> = {
			[CustomNotification.Projects]: {
				title: 'NEW_NOTIFICATIONS.MIGRATION_SUCCESS.TITLE',
				message: ''
			},
			[CustomNotification.Routine]: {
				title: routineTitleMap[value] ?? '',
				message: ''
			},
			[CustomNotification.DocTools]: {
				title: 'NEW_NOTIFICATIONS.DOCUMENT_READY_TOAST.TITLE',
				message: 'NEW_NOTIFICATIONS.DOCUMENT_READY_TOAST.MESSAGE'
			}
		};

		const { title, message } = NOTIFICATION_TO_TITLE_MAP[key] ?? { title: '', message: '' };

		return {
			title: title.length ? this.translationService.instant(title) : '',
			message: message.length ? this.translationService.instant(message) : ''
		};
	}

	private handleCustomNotification(notification: MappedNotification): void {
		const [[key, value]] = Object.entries(notification.message.message_type);
		switch (key) {
			case CustomNotification.Projects:
				this.createNotification(notification);
				this.passDataService.send(PassDataKeys.ImportProjectItems);
				break;
			case CustomNotification.Routine:
				this.createNotification(notification);
				break;
			default:
				this.createNotification(notification);
				break;
		}
	}

	private buildToastUrl(notification: Notification): ToastUrl | null {
		let type: UrlType;
		let { url, urls, urlCallback }: { url: string | null; urls: string[] | null; urlCallback: () => void } = {
			url: null,
			urls: null,
			urlCallback: null
		};
		let { queryParams, queryParamsAsString }: { queryParams: Params; queryParamsAsString: string } = {
			queryParams: {},
			queryParamsAsString: ''
		};

		switch (notification.actualType) {
			case NotificationTypeEnum.MigrationCheckFail:
				const notificationAction: NotificationOpenModalAction = {
					openMigrationModal: true,
					notification: notification
				};
				urlCallback = () => (this.notificationActionService.toggleMigrationModal = notificationAction);
				return { type: UrlType.SameTab, urlCallback };
			case NotificationTypeEnum.MigrationCheck:
				if (notification.canMigrated) {
					const path: string = RunMigrationUrl[DataMigrationTypesEnum[notification.message.data_migration?.type]];
					urlCallback = () =>
						this.migrationsDataService
							.runMigrationFile(notification.message.data_migration?.id, path)
							.pipe(
								tap(() => {
									const { TITLE: title, MESSAGE: message }: { TITLE: string; MESSAGE: string } =
										this.translationService.instant('NEW_NOTIFICATIONS.MIGRATION_FILE_IN_PROCESS');
									this.showToast(ToastType.Info, message, title);
								})
							)
							.subscribe();
					return { type: UrlType.SameTab, urlCallback };
				}
				break;
		}

		if (notification.message.link) {
			url = notification.message.link;
			type = notification.message.new_tab
				? UrlType.NewTab
				: notification.message.redirect
					? UrlType.SameTab
					: UrlType.Download;
			return { type, url };
		}

		if (notification.message.links) {
			urls = notification.message.links;
			type = UrlType.NewTab;
			return { type, urls };
		}

		switch (notification.message.redirect) {
			case NotificationRedirectType.Task:
				const { task_id, sub_task_id } = notification.message;
				queryParams = {
					...(sub_task_id ? { sub_task: sub_task_id } : { task: task_id })
				};
				queryParamsAsString = new URLSearchParams(queryParams).toString();
				url = `${MainRoutes.Tasks}/${TasksRoute.Main}?${queryParamsAsString}`;
				type = UrlType.NewTab;
				break;
			case NotificationRedirectType.ProformaInvoices:
				queryParams = {
					users: notification.message.users[0],
					clients: notification.message.clients.map((client: number) => client),
					presentation_date: notification.message.presentation_date
				};
				queryParamsAsString = new URLSearchParams(queryParams).toString();
				url = `${MainRoutes.Management}/${FinanceRoutes.ProformaInvoice}?${queryParamsAsString}`;
				type = UrlType.NewTab;
				break;
			default:
				return null;
		}
		return { type, url, urlCallback };
	}

	private handlePdfNotification(notification: MappedNotification): void {
		if (notification.message.proforma_invoice_id) {
			const data: Params = {
				proformaInvoiceId: notification.message.proforma_invoice_id,
				documentId: notification.message.document_id
			};
			this.passDataService.changeData(data);
		}
	}

	private getMessageWithIds(message: string, ids: (number | string)[]): string {
		const idsString: string = ids.join(', ');
		return `${message} ${idsString}`;
	}

	private openGreenLaneNotSentModal(notification: Notification): void {
		const translation: { [key: string]: any } = this.translationService.instant('MODALS.GREEN_LANE_ERRORS');
		let initialData: Params = {
			subtitle: translation.SUBTITLE,
			submitButtonLabel: translation.REDIRECT
		};
		if (notification.message.green_lane_disabled?.length) {
			initialData = {
				...initialData,
				contentTitle: this.getMessageWithIds(translation.CONTENT_TITLE, notification.message.green_lane_disabled)
			};
		}
		if (notification.message.missing_contacts?.length) {
			initialData = {
				...initialData,
				content: this.getMessageWithIds(translation.CONTENT, notification.message.missing_contacts)
			};
		}

		this.modalService.openModal(ModalType.NewConfirmClose, initialData).subscribe((confirmRedirect: boolean): void => {
			if (confirmRedirect) {
				const toastUrl: ToastUrl = this.buildToastUrl(notification);
				window.open(toastUrl.url, '_blank');
			}
		});
	}

	private handleGreenLaneNotification(notification: MappedNotification): void {
		const invoicesNotSent: boolean = !!notification.message.pis_not_sent;
		const invoicesSent: boolean = !!notification.message.pis_sent;
		if (invoicesNotSent && document.hasFocus()) {
			this.openGreenLaneNotSentModal(notification);
		}
		if (invoicesSent) {
			this.createNotification(notification);
		}
	}

	private openEmailsNotSentModal(notification: Notification): void {
		const translation: { [key: string]: any } = this.translationService.instant('MODALS.EMAIL_ERRORS');
		const skippedEmails: string[] = notification.message.skipped_to;
		const initialData: Params = {
			subtitle: translation.SUBTITLE,
			contentTitle: this.getMessageWithIds('', skippedEmails)
		};

		this.modalService.openModal(ModalType.NewConfirmClose, initialData).subscribe();
	}

	private handleEmailNotification(notification: MappedNotification): void {
		const [[key, value]] = Object.entries(notification.message.message_type);

		switch (value) {
			case CustomNotificationType.Valid:
				this.createNotification(notification);
				break;
			case CustomNotificationType.Invalid:
				const title: string = this.translationService.instant('TOAST.FAILURE');
				const message: string = this.translationService.instant('ERRORS.EMAILS_NOT_VALID');
				this.showToast(ToastType.Error, message, title);
				break;
			case CustomNotificationType.PartialValid:
				this.createNotification(notification);
				if (document.hasFocus()) {
					this.openEmailsNotSentModal(notification);
				}
				break;
		}
	}

	private navigateToLink(notification: MappedNotification): void {
		if (document.hasFocus()) {
			window.open(notification.message.link, 'blank');
		}
	}

	private navigateToConflictCheck(notification: MappedNotification): void {
		if (document.hasFocus()) {
			const routerUrl: string = this.router.url;
			if (routerUrl.includes(`${ClientsAndMattersRoute.ConflictCheck}/`)) {
				this.passDataService.changeData({ refreshTable: true });
			} else {
				const navigationPath: string = `${MainRoutes.Clients}/${ClientsAndMattersRoute.Search}/${ClientsAndMattersRoute.ConflictCheck}/${notification.message.conflict_check.id}`;
				window.open(navigationPath, '_blank');
			}
		}
	}

	private navigateToClientOrMatter(notification: MappedNotification): void {
		if (document.hasFocus()) {
			const entityRoute: string = notification.message.matter
				? ClientsAndMattersRoute.Matter
				: ClientsAndMattersRoute.Client;
			const entityId: number = notification.message.matter
				? notification.message.matter.id
				: notification.message.client.id;
			const navigationPath: string = `${MainRoutes.Clients}/${entityRoute}/${entityId}`;
			window.open(navigationPath, '_blank');
		}
		this.createNotification(notification);
	}

	private showToast(
		type: ToastType,
		message?: string,
		title?: string,
		payload?: ToastPayload,
		duration?: number
	): void {
		switch (type) {
			case ToastType.Info:
				this.toastService.customInfo(message, title, payload, duration);
				break;
			case ToastType.Success:
				this.toastService.customSuccess(message, title, payload, duration);
				break;
			case ToastType.Warning:
				this.toastService.customWarn(message, title, payload, duration);
				break;
			case ToastType.Error:
				this.toastService.customError(message, title, payload, duration);
				break;
			default:
				this.toastService.customNotification(message, title, payload, duration);
				break;
		}
	}

	private createNotification(notification: MappedNotification): void {
		const { title, message }: { title: string; message: string } = this.getTitleAndMessage(notification);
		const payload: ToastPayload = {
			notification,
			toastUrl: this.buildToastUrl(notification),
			removeTimeStamp: notification.removeTimeStamp
		};
		const type: ToastType = this.resolveToastType(notification);
		this.showToast(type, message, title, payload);
	}

	private resolveToastType(notification: MappedNotification): ToastType {
		if (notification.actualType === NotificationTypeEnum.Custom) {
			const [[key]] = Object.entries(notification.message.message_type);
			return (
				MAP_CUSTOM_NOTIFICATION_TOAST_TYPE[key as CustomNotification] ??
				MAP_NOTIFICATION_TOAST_TYPE[notification.actualType]
			);
		}
		return MAP_NOTIFICATION_TOAST_TYPE[notification.actualType];
	}

	private getTime(date: string): string {
		const activeTime = `${getHours(date) < 10 ? `0${getHours(date)}` : `${getHours(date)}`}:${
			getMinutes(date) < 10 ? `0${getMinutes(date)}` : `${getMinutes(date)}`
		}`;

		if (isToday(date)) {
			return `${this.translationService.instant('NOTIFICATIONS.DROP_DOWN_MENU.TODAY')}, ${activeTime}`;
		} else if (isYesterday(date)) {
			return `${this.translationService.instant('NOTIFICATIONS.DROP_DOWN_MENU.YESTERDAY')}, ${activeTime}`;
		} else {
			return `${format(date, this.displayFormat)}, ${activeTime}`;
		}
	}

	private getNotificationTitle(notification: Notification, activeNotification: NotificationTypesModel): string {
		if (activeNotification) {
			switch (activeNotification.type) {
				case NotificationTypeEnum.Reminder:
					return notification.message.task_name ?? notification.message.sub_task_name;
				case NotificationTypeEnum.Custom:
					return this.getCustomNotificationTitle(notification);
				default:
					return this.currentLang === Languages.HE ? activeNotification.alt_name : activeNotification.name;
			}
		}
		return '';
	}

	private getCustomNotificationTitle(notification: Notification): string {
		let title: string = '';
		const [[key, value]] = Object.entries(notification.message.message_type);

		switch (key) {
			case CustomNotification.Projects:
				title = 'NEW_NOTIFICATIONS.MIGRATION_SUCCESS.TITLE';
				break;
			case CustomNotification.Routine:
				const routineToTitleMap: Record<string, string> = {
					good_morning: 'NEW_NOTIFICATIONS.GOOD_MORNING',
					daily: 'NEW_NOTIFICATIONS.DAILY',
					stretch: 'NEW_NOTIFICATIONS.STRETCH'
				};
				title = routineToTitleMap[value];
				break;
			case CustomNotification.DocTools:
				title = 'NEW_NOTIFICATIONS.WORD_FILE_READY';
				break;
			default:
				title = 'NEW_NOTIFICATIONS.DEFAULT_CUSTOM';
				break;
		}

		return this.translationService.instant(title);
	}

	private getIcon(notification: Notification, activeNotification: NotificationTypesModel): string {
		if (activeNotification && activeNotification.icon) {
			if (activeNotification.type === NotificationTypeEnum.Custom) {
				const [[key]] = Object.entries(notification.message.message_type);
				return MAP_CUSTOM_NOTIFICATION_ICON_TYPE[key];
			}
			return NotificationIconType[activeNotification.icon];
		}
		return '';
	}

	private openNewReportModal(notification: Notification): void {
		const matter: MatterAutocompleteV3Option = notification.message.report?.matter;
		const hours: number = notification.message.report?.hours;
		const modalConfig: ReportModalConfig = {
			reportData: {
				client: notification.message.report.client,
				matter: notification.message.report.matter,
				details: notification.message.report.details,
				...(hours
					? {
							time_report: { hours: hours, hours_override: matter && !matter.billable ? 0 : hours }
						}
					: {})
			}
		};
		this.newEntryService.handleNewReportModal(modalConfig);
	}

	public mapNotification(notification: Notification): MappedNotification {
		const canMigrated: boolean = migrationExportTypes.find(
			item => item.id === notification.message.data_migration?.type
		)?.enable_upload;
		const activeNotification: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		const actualType: number = activeNotification?.type;

		return {
			...notification,
			time: notification.created_at ? this.getTime(notification.created_at) : '',
			iconName: this.getIcon(notification, activeNotification),
			title: this.getNotificationTitle(notification, activeNotification),
			actualType,
			canMigrated,
			removeTimeStamp: actualType === NotificationTypeEnum.Accounting
		} as MappedNotification;
	}

	public handleNewNotification(notification: MappedNotification): void {
		switch (notification.actualType) {
			case NotificationTypeEnum.Custom:
				this.handleCustomNotification(notification);
				break;
			case NotificationTypeEnum.MattersError:
				this.toastService.error();
				break;
			case NotificationTypeEnum.ClientsMattersError:
				const errorCode: ErrorCode =
					(notification.message?.error as CustomErrorMessages)?.code ?? ErrorCode.ContactSupport;
				this.toastService.error(errorCode);
				break;
			case NotificationTypeEnum.LinkReady:
				this.navigateToLink(notification);
				break;
			case NotificationTypeEnum.ConflictCheck:
				this.navigateToConflictCheck(notification);
				break;
			case NotificationTypeEnum.ClientsMatters:
			case NotificationTypeEnum.Matters:
				this.navigateToClientOrMatter(notification);
				break;
			case NotificationTypeEnum.Pdf:
				this.handlePdfNotification(notification);
				this.createNotification(notification);
				break;
			case NotificationTypeEnum.ProformaInvoicesGreenLane:
				this.handleGreenLaneNotification(notification);
				break;
			case NotificationTypeEnum.Email:
				if (notification.message.message_type) {
					this.handleEmailNotification(notification);
				} else {
					this.createNotification(notification);
				}
				break;
			case NotificationTypeEnum.Admin:
				this.passDataService.changeData({ clientsAndMattersVatUpdate: true });
				this.createNotification(notification);
				break;
			default:
				this.createNotification(notification);
				break;
		}
	}

	public handleNewHiddenNotification(notification: Notification): void {
		// Used for hidden notifications in the dropdown like DMS Report
		switch (notification.type) {
			case NotificationTypeEnum.DmsReport:
				if (document.hasFocus()) {
					this.openNewReportModal(notification);
				}
				break;
			default:
				break;
		}
	}
}
